import React from "react";
import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div className="legal-page page-shell">
      <div className="card legal-card">
        <h1>Terms of Service</h1>
        <p className="muted">Last updated: May 4, 2026</p>

        <p>
          Welcome to MyFarmingPal. These Terms of Service govern your access to
          and use of MyFarmingPal, including our website, marketplace, job
          listings, messaging, event tools, profiles, reviews, and related
          features.
        </p>

        <p>
          By creating an account or using MyFarmingPal, you agree to these
          Terms. If you do not agree, do not use the service.
        </p>

        <h2>1. What MyFarmingPal Is</h2>
        <p>
          MyFarmingPal is an online platform that helps farmers, landowners,
          homesteaders, farm workers, vendors, event hosts, and local community
          members connect with each other. Users may post or browse field jobs,
          day jobs, farm products, community events, vendor opportunities, and
          related listings.
        </p>

        <p>
          MyFarmingPal is a connection platform only. We are not an employer,
          contractor, staffing agency, payment processor, delivery provider,
          inspector, insurer, broker, veterinarian, transportation service, or
          party to any agreement between users.
        </p>

        <h2>Trust, Visibility, and Platform Status</h2>
        <p>
          MyFarmingPal is privately built and locally focused. It is not a
          government program and is not government-operated. MyFarmingPal does
          not sell users' personal data.
        </p>

        <p>
          Public visibility is intended to be based on local relevance,
          category, distance, freshness, and user-selected filters, not on
          paying to force a post above other local users.
        </p>

        <h2>2. Eligibility</h2>
        <p>
          You must be at least 18 years old to create an account or use
          MyFarmingPal. By using the service, you confirm that you are at least
          18 and legally able to enter into agreements.
        </p>

        <h2>Adult Accounts and Teen Work Opportunities</h2>
        <p>
          MyFarmingPal accounts are intended for users who are at least 18 years
          old. Teenagers should not create their own accounts. A parent or
          legal guardian may use their own adult account to help a teenager look
          for summer work, farm help, or other local work opportunities.
        </p>

        <p>
          The adult account holder is responsible for the minor's use of the
          platform and for deciding whether any opportunity is safe, legal,
          appropriate, and permitted. Parents, guardians, workers, landowners,
          farmers, and hiring parties are solely responsible for discussing and
          handling permission, supervision, transportation, safety, pay, taxes,
          insurance, work conditions, equipment, qualifications, and compliance
          with all applicable federal, state, and local laws, including labor,
          child labor, agricultural, safety, and tax laws.
        </p>

        <p>
          MyFarmingPal is a connection platform only. MyFarmingPal is not an
          employer, hiring agency, staffing agency, payroll provider, payment
          processor, supervisor, insurer, legal guardian, or party to any work
          agreement between users. MyFarmingPal does not set wages, collect or
          guarantee payment for work, guarantee work, verify worker eligibility,
          verify licenses, conduct background checks, inspect job sites,
          supervise work, or determine whether a person is legally allowed or
          qualified to perform a job.
        </p>

        <p>
          Users should use careful judgment, ask questions, verify details, and
          follow all applicable laws before agreeing to any work arrangement.
        </p>

        <h2>3. User Accounts</h2>
        <p>
          You are responsible for your account, login information, profile
          information, listings, messages, reviews, and all activity that occurs
          under your account. You agree to provide accurate information and to
          keep your account secure.
        </p>

        <h2>4. User Listings and Content</h2>
        <p>
          Users are responsible for the content they post, including job
          listings, field details, product listings, prices, photos, event
          information, messages, reviews, and profile information. You may not
          post false, misleading, illegal, unsafe, stolen, infringing,
          discriminatory, threatening, harassing, or abusive content.
        </p>

        <p>
          By posting content on MyFarmingPal, you give us permission to display,
          store, and use that content to operate, improve, promote, and protect
          the service.
        </p>

        <h2>5. Jobs and Work Arrangements</h2>
        <p>
          MyFarmingPal does not employ users and does not control or supervise
          work performed by users. Any job, bid, application, price, schedule,
          equipment use, safety requirement, licensing requirement, tax
          obligation, insurance obligation, or work agreement is solely between
          the users involved.
        </p>

        <p>
          Users are responsible for determining whether a person is qualified,
          properly licensed, insured, experienced, and legally allowed to
          perform a job. Users are also responsible for complying with all
          federal, state, and local laws, including labor, tax, safety,
          environmental, agricultural, animal welfare, transportation, and
          equipment-related laws.
        </p>

        <h2>6. Products and Marketplace Listings</h2>
        <p>
          MyFarmingPal allows users to list and request local farm goods and
          related products. Sellers are solely responsible for the accuracy,
          safety, legality, quality, labeling, pricing, availability, delivery,
          and fulfillment of their listings.
        </p>

        <p>
          Buyers are responsible for reviewing listings, asking questions, and
          deciding whether to purchase from a seller. MyFarmingPal does not
          guarantee any product, seller, buyer, description, price, delivery,
          transaction, or outcome.
        </p>

        <h2>7. Events and Reservations</h2>
        <p>
          Event hosts are responsible for event accuracy, safety, location,
          vendor spaces, booth availability, approvals, payment collection, and
          compliance with applicable laws, permits, and venue requirements.
          Vendors and attendees are responsible for deciding whether to
          participate in an event.
        </p>

        <h2>8. Payments</h2>
        <p>
          Unless a payment feature is clearly provided through MyFarmingPal,
          payments are arranged directly between users. MyFarmingPal is not
          responsible for unpaid work, refunds, chargebacks, pricing disputes,
          product disputes, reservation disputes, or payment collection between
          users.
        </p>

        <h2>9. Reviews and Ratings</h2>
        <p>
          Reviews must be honest, based on real interactions, and not used to
          harass, threaten, mislead, or retaliate against others. We may remove
          reviews that appear fraudulent, abusive, irrelevant, or in violation
          of these Terms.
        </p>

        <h2>10. Safety</h2>
        <p>
          Farm work and rural property work can involve risks, including heavy
          equipment, animals, tools, chemicals, uneven terrain, weather, road
          travel, and physical labor. Users are responsible for their own
          safety, judgment, training, protective equipment, transportation,
          insurance, and legal compliance.
        </p>

        <p>
          MyFarmingPal does not conduct background checks, identity
          verification, license verification, insurance verification, job site
          inspections, or product inspections unless we clearly state otherwise.
        </p>

        <h2>11. Prohibited Conduct</h2>
        <p>You may not use MyFarmingPal to:</p>

        <ul>
          <li>Post false, deceptive, unsafe, or illegal listings.</li>
          <li>Sell stolen, illegal, counterfeit, or prohibited goods.</li>
          <li>Harass, threaten, abuse, impersonate, or discriminate against others.</li>
          <li>Upload malicious code, spam, scams, or fraudulent content.</li>
          <li>Attempt to access another user’s account or private data.</li>
          <li>Use the service for unlawful activity.</li>
          <li>Interfere with the security or operation of the service.</li>
        </ul>

        <h2>12. Account Suspension or Removal</h2>
        <p>
          We may remove content, restrict features, suspend accounts, or
          terminate access if we believe a user has violated these Terms, harmed
          another user, created risk for the platform, or used the service in a
          way that is unsafe, abusive, deceptive, or unlawful.
        </p>

        <h2>13. No Guarantees</h2>
        <p>
          MyFarmingPal is provided “as is” and “as available.” We do not
          guarantee that the service will be uninterrupted, error-free, secure,
          profitable, available in all areas, or suitable for any particular
          purpose.
        </p>

        <h2>14. Limitation of Liability</h2>
        <p>
          To the fullest extent allowed by law, MyFarmingPal and its owners,
          operators, employees, contractors, and partners are not liable for
          indirect, incidental, special, consequential, exemplary, or punitive
          damages, or for losses related to user interactions, jobs, products,
          payments, events, property damage, personal injury, disputes, lost
          profits, lost data, or service interruptions.
        </p>

        <h2>15. User Disputes</h2>
        <p>
          Users are responsible for resolving disputes with each other.
          MyFarmingPal may, but is not required to, help review reports or take
          platform action. We are not responsible for enforcing private
          agreements between users.
        </p>

        <h2>16. Changes to the Service or Terms</h2>
        <p>
          We may update MyFarmingPal or these Terms from time to time. Continued
          use of the service after changes are posted means you accept the
          updated Terms.
        </p>

        <h2>17. Contact</h2>
        <p>
          Questions about these Terms can be sent through our{" "}
          <Link to="/contact">Contact page</Link>.
        </p>
      </div>
    </div>
  );
};

export default Terms;
